/**
 * Service isolado para o endpoint de listagem operativa de usuários.
 * Responsabilidade única: montar a requisição e validar o status HTTP.
 * Toda regra de negócio/estado vive no hook useUsuariosOperativa.
 */
import apiUsuario from "./apiUsuario";
import {
  FiltrosOperativa,
  RespostaOperativa,
  SistemaItem,
} from "../interface/usuarioOperativa";

// Erro único com discriminador — evita múltiplas classes no mesmo arquivo
export type TipoErroOperativa =
  | "sessao_expirada"
  | "sem_permissao"
  | "erro_servidor";

export class OperativaServiceError extends Error {
  constructor(public readonly tipo: TipoErroOperativa, message?: string) {
    super(message ?? tipo);
    this.name = "OperativaServiceError";
  }
}

/**
 * Serializa params para query string.
 * Arrays são repetidos: { sistemas: [1,3] } → "sistemas=1&sistemas=3"
 * conforme contrato do backend (/user/operativa).
 */
function serializarParams(params: Record<string, unknown>): string {
  const partes: string[] = [];
  Object.entries(params).forEach(([chave, valor]) => {
    if (Array.isArray(valor)) {
      valor.forEach((v) =>
        partes.push(`${encodeURIComponent(chave)}=${encodeURIComponent(v)}`)
      );
    } else if (valor !== undefined && valor !== null) {
      partes.push(
        `${encodeURIComponent(chave)}=${encodeURIComponent(String(valor))}`
      );
    }
  });
  return partes.join("&");
}

export async function fetchUsuariosOperativa(
  filtros: Partial<FiltrosOperativa>,
  token: string
): Promise<RespostaOperativa> {
  // Remove campos vazios, nulos e arrays vazios para não poluir a query string
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => {
      if (v === "" || v === null || v === undefined) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
  ) as Record<string, unknown>;

  const response = await apiUsuario.get<RespostaOperativa>("/user/operativa", {
    params,
    paramsSerializer: serializarParams,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401)
    throw new OperativaServiceError("sessao_expirada");
  if (response.status === 403) throw new OperativaServiceError("sem_permissao");
  if (response.status !== 200)
    throw new OperativaServiceError("erro_servidor", `HTTP ${response.status}`);

  return response.data;
}

export async function fetchSistemas(token: string): Promise<SistemaItem[]> {
  const response = await apiUsuario.get<SistemaItem[]>("/sistemas", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401)
    throw new OperativaServiceError("sessao_expirada");
  if (response.status !== 200)
    throw new OperativaServiceError("erro_servidor", `HTTP ${response.status}`);

  return response.data;
}
